import React from "react";
import { toast } from "sonner";
import type { Channel, MessageComposer, TextComposer } from "stream-chat";
import { useTranslation } from "react-i18next";
import { useMessagingChannelConfigs } from "@/components/pages/communication-channels/hooks/messaging-channel-configs.hooks";
import {
  ChannelHint,
  MAX_EXTERNAL_MESSAGE_LENGTH,
  SendExternalChatMedia,
} from "@/types/chat-external-message";
import { ExternalCommunicationChannelKind } from "@/types/messaging-channel-config";
import { getHttpStatus } from "@/utils/httpErrors";
import { classifySendError } from "@/services/whatsapp-service";
import { useSendExternalMessage } from "./use-send-external-message.hooks";
import { ComposerSendIndicatorState } from "@/components/pages/chats/component/chat-custom-components/ComposerSendStatusIndicator";
import { isProviderUnavailable } from "@/components/pages/chats/component/chat-custom-components/utils/isProviderUnavailable";

/** Dead-end `409` refusals (no template can fix them) → specific toast copy. */
const REFUSAL_TOAST_KEY: Record<string, string> = {
  WHATSAPP_DISABLED: "communicationChannels.composer.error.whatsappDisabled",
  KILL_SWITCH: "communicationChannels.composer.error.whatsappKillSwitch",
  WHATSAPP_UNSUPPORTED_MEDIA_TYPE:
    "communicationChannels.composer.error.unsupportedWhatsappMedia",
};

interface ComposerAttachmentLike {
  asset_url?: string;
  image_url?: string;
  mime_type?: string;
  title?: string;
  type?: string;
  localMetadata?: {
    id?: string;
    uploadState?: string;
    file?: {
      name?: string;
      type?: string;
    };
  };
}

const getAttachmentUrl = (attachment: ComposerAttachmentLike): string | null =>
  attachment.asset_url || attachment.image_url || null;

const getAttachmentMimeType = (
  attachment: ComposerAttachmentLike,
): string | null =>
  attachment.mime_type || attachment.localMetadata?.file?.type || null;

const getAttachmentFilename = (
  attachment: ComposerAttachmentLike,
): string | undefined =>
  attachment.title || attachment.localMetadata?.file?.name || undefined;

const SUPPORTED_WHATSAPP_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
]);

const SUPPORTED_WHATSAPP_DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/rtf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
]);

const isSupportedWhatsAppMedia = (
  attachment: ComposerAttachmentLike,
): boolean => {
  const mimeType = getAttachmentMimeType(attachment)
    ?.split(";")[0]
    ?.trim()
    .toLowerCase();
  if (!mimeType) return false;
  return (
    SUPPORTED_WHATSAPP_IMAGE_MIME_TYPES.has(mimeType) ||
    SUPPORTED_WHATSAPP_DOCUMENT_MIME_TYPES.has(mimeType)
  );
};

interface UseMultiChannelComposerArgs {
  enabled: boolean;
  hotelId: string | null;
  channel: Channel | undefined;
  messageComposer: MessageComposer;
  textComposer: TextComposer;
  recordingActive: boolean;
  streamHandleSubmit: () => void;
}

export const useMultiChannelComposer = ({
  enabled,
  hotelId,
  channel,
  messageComposer,
  textComposer,
  recordingActive,
  streamHandleSubmit,
}: UseMultiChannelComposerArgs) => {
  const { t } = useTranslation();
  const { data: channelConfigs = [] } = useMessagingChannelConfigs(
    enabled ? hotelId : null,
  );
  const [selectedHint, setSelectedHint] = React.useState<ChannelHint>("auto");
  const [unavailableChannels, setUnavailableChannels] = React.useState<
    ReadonlySet<ExternalCommunicationChannelKind>
  >(() => new Set());
  const [indicatorState, setIndicatorState] =
    React.useState<ComposerSendIndicatorState>({ kind: "idle" });
  const [reengageOpen, setReengageOpen] = React.useState(false);
  const indicatorTimerRef = React.useRef<number | null>(null);

  const clearIndicatorTimer = React.useCallback(() => {
    if (indicatorTimerRef.current !== null) {
      window.clearTimeout(indicatorTimerRef.current);
      indicatorTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => () => clearIndicatorTimer(), [clearIndicatorTimer]);

  React.useEffect(() => {
    setSelectedHint("auto");
    setUnavailableChannels(new Set());
    clearIndicatorTimer();
    setIndicatorState({ kind: "idle" });
    setReengageOpen(false);
  }, [channel?.id, clearIndicatorTimer]);

  const { mutateAsync: sendExternal, isPending: isSending } =
    useSendExternalMessage();

  const handleProviderUnavailable = React.useCallback(() => {
    if (selectedHint === "auto" || selectedHint === "STREAM") {
      setIndicatorState({ kind: "idle" });
      return;
    }
    const failedKind: ExternalCommunicationChannelKind = selectedHint;
    setUnavailableChannels((prev) => {
      const next = new Set(prev);
      next.add(failedKind);
      return next;
    });
    setSelectedHint("auto");
    toast.error(
      t("communicationChannels.composer.error.providerMissing", {
        channel: t(`communicationChannels.channels.${failedKind}.label`),
      }),
    );
    setIndicatorState({ kind: "idle" });
  }, [selectedHint, t]);

  const handleFallback = React.useCallback(
    (statusCode: number) => {
      toast.warning(
        statusCode === 503
          ? t("communicationChannels.composer.fallback.killSwitch")
          : t("communicationChannels.composer.fallback.featureOff"),
      );
      setIndicatorState({ kind: "warning" });
      clearIndicatorTimer();
      indicatorTimerRef.current = window.setTimeout(() => {
        setIndicatorState({ kind: "idle" });
        indicatorTimerRef.current = null;
      }, 3500);
      streamHandleSubmit();
    },
    [clearIndicatorTimer, streamHandleSubmit, t],
  );

  const handleSendError = React.useCallback(
    (err: unknown) => {
      const statusCode = getHttpStatus(err);
      if (statusCode === 503 || statusCode === 403) {
        handleFallback(statusCode);
        return;
      }
      if (statusCode === 400 && isProviderUnavailable(err)) {
        handleProviderUnavailable();
        return;
      }
      const classified = classifySendError(err);
      if (classified.kind === "reengage") {
        // NO_CONVERSATION / WINDOW_CLOSED / RE_ENGAGEMENT_REQUIRED /
        // RECIPIENT_NOT_OPTED_IN — free text can't reach the guest, but an
        // approved template can open or reopen the chat. The typed text is left
        // in place only as a reference; the template is what the guest actually
        // receives.
        setIndicatorState({ kind: "idle" });
        setReengageOpen(true);
        return;
      }
      if (classified.kind === "upstream") {
        // 502 — the WhatsApp integration itself failed; the message never left
        // the building and a template won't help. Needs an admin, not a retry.
        toast.error(
          t(
            classified.token === "WHATSAPP_AUTH_FAILED"
              ? "communicationChannels.composer.error.whatsappAuthFailed"
              : "communicationChannels.composer.error.whatsappUnavailable",
          ),
        );
        setIndicatorState({ kind: "idle" });
        return;
      }
      // Any other 409 (disabled / kill switch) or unknown error → toast, no picker.
      toast.error(
        t(
          REFUSAL_TOAST_KEY[classified.token] ??
            "communicationChannels.composer.error.generic",
        ),
      );
      setIndicatorState({ kind: "idle" });
    },
    [handleFallback, handleProviderUnavailable, t],
  );

  const hasNonTextPayload = React.useCallback((): boolean => {
    const attachmentCount =
      messageComposer?.attachmentManager?.attachments?.length ?? 0;
    const quoted = !!(messageComposer as unknown as { quotedMessage?: unknown })
      .quotedMessage;
    return attachmentCount > 0 || recordingActive || quoted;
  }, [messageComposer, recordingActive]);

  const getSingleWhatsAppMedia = React.useCallback(
    (caption?: string): SendExternalChatMedia | null => {
      const attachments = (messageComposer?.attachmentManager?.attachments ??
        []) as ComposerAttachmentLike[];
      const quoted = !!(
        messageComposer as unknown as { quotedMessage?: unknown }
      ).quotedMessage;
      if (attachments.length !== 1 || recordingActive || quoted) return null;

      const attachment = attachments[0];
      const url = getAttachmentUrl(attachment);
      const mimeType = getAttachmentMimeType(attachment);
      const uploadState = attachment.localMetadata?.uploadState;

      if (!url || !mimeType || uploadState === "uploading") return null;
      if (!isSupportedWhatsAppMedia(attachment)) return null;

      return {
        url,
        mimeType,
        ...(caption ? { caption } : {}),
        ...(getAttachmentFilename(attachment)
          ? { filename: getAttachmentFilename(attachment) }
          : {}),
      };
    },
    [messageComposer, recordingActive],
  );

  const clearComposerAttachments = React.useCallback(() => {
    const attachments = (messageComposer?.attachmentManager?.attachments ??
      []) as ComposerAttachmentLike[];
    const ids = attachments
      .map((attachment) => attachment.localMetadata?.id)
      .filter((id): id is string => !!id);
    if (ids.length > 0) {
      messageComposer.attachmentManager.removeAttachments(ids);
    }
  }, [messageComposer]);

  const send = React.useCallback(async (): Promise<void> => {
    if (!enabled || !channel?.id) {
      streamHandleSubmit();
      return;
    }

    const trimmed = (textComposer.text ?? "").trim();
    if (textComposer.command || trimmed.startsWith("/")) {
      streamHandleSubmit();
      return;
    }

    if (hasNonTextPayload() && selectedHint === "STREAM") {
      streamHandleSubmit();
      return;
    }

    const media = hasNonTextPayload()
      ? getSingleWhatsAppMedia(trimmed || undefined)
      : null;

    if (hasNonTextPayload() && !media) {
      if (selectedHint === "WHATSAPP") {
        toast.error(
          t("communicationChannels.composer.error.unsupportedWhatsappMedia"),
        );
        setIndicatorState({ kind: "idle" });
        return;
      }
      toast.warning(t("communicationChannels.composer.fallback.attachment"));
      streamHandleSubmit();
      return;
    }

    const text =
      trimmed ||
      media?.filename ||
      t("communicationChannels.composer.mediaFallbackText");

    if (!text) return;
    if (text.length > MAX_EXTERNAL_MESSAGE_LENGTH) {
      toast.error(t("communicationChannels.composer.error.tooLong"));
      return;
    }

    clearIndicatorTimer();
    setIndicatorState({ kind: "sending", hint: selectedHint });
    try {
      const res = await sendExternal({
        streamChannelId: channel.id,
        text,
        channel: selectedHint,
        ...(media ? { media } : {}),
      });
      textComposer.setText("");
      if (media) {
        clearComposerAttachments();
      }
      setIndicatorState({ kind: "sent", resolved: res.channel });
      indicatorTimerRef.current = window.setTimeout(() => {
        setIndicatorState({ kind: "idle" });
        indicatorTimerRef.current = null;
      }, 1500);
    } catch (err) {
      handleSendError(err);
    }
  }, [
    enabled,
    channel?.id,
    hasNonTextPayload,
    getSingleWhatsAppMedia,
    streamHandleSubmit,
    textComposer,
    clearIndicatorTimer,
    selectedHint,
    sendExternal,
    clearComposerAttachments,
    handleSendError,
    t,
  ]);

  return {
    configs: channelConfigs,
    selectedHint,
    setSelectedHint,
    unavailableChannels,
    indicatorState,
    isSending,
    send,
    reengageOpen,
    setReengageOpen,
  };
};
